import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  Heading,
  HStack,
  Image,
  Spinner,
  Button,
  Collapse,
  useDisclosure
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useArticle } from '../hooks/useApi';
import ArticleAudioPlayer from '../components/ArticleAudioPlayer';

// Helper function to generate slug from text
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Helper function to extract headings from HTML content
const extractHeadings = (htmlContent) => {
  if (!htmlContent) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const headings = doc.querySelectorAll('h1, h2, h3');

  const tocItems = [];
  const slugCounts = {};

  headings.forEach((heading) => {
    const text = heading.textContent.trim();

    // Skip empty headings
    if (!text) return;

    let slug = generateSlug(text);

    // Handle duplicate slugs
    if (slugCounts[slug]) {
      slugCounts[slug]++;
      slug = `${slug}-${slugCounts[slug]}`;
    } else {
      slugCounts[slug] = 1;
    }

    tocItems.push({
      id: slug,
      text: text,
      level: parseInt(heading.tagName.charAt(1))
    });
  });

  return tocItems;
};

// Helper function to add IDs to headings in HTML content
const addHeadingIds = (htmlContent) => {
  if (!htmlContent) return htmlContent;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const headings = doc.querySelectorAll('h1, h2, h3');

  const slugCounts = {};

  headings.forEach((heading) => {
    const text = heading.textContent.trim();
    let slug = generateSlug(text);

    // Handle duplicate slugs
    if (slugCounts[slug]) {
      slugCounts[slug]++;
      slug = `${slug}-${slugCounts[slug]}`;
    } else {
      slugCounts[slug] = 1;
    }

    heading.id = slug;
  });

  return doc.body.innerHTML;
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const { data: article, loading, error } = useArticle(slug);
  const contentRef = useRef(null);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  // Extract TOC items from article content
  const tocItems = useMemo(() => {
    if (!article?.content) return [];
    return extractHeadings(article.content);
  }, [article?.content]);

  // Process content to add IDs to headings
  const processedContent = useMemo(() => {
    if (!article?.content) return '';
    return addHeadingIds(article.content);
  }, [article?.content]);

  // Smooth scroll to heading
  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for any fixed headers
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box py={20} px={4}>
        <VStack spacing={8} maxW="4xl" mx="auto" textAlign="center">
          <Spinner size="xl" color="green.500" thickness="4px" />
          <Text color="gray.300">Loading article...</Text>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <Box py={20} px={4}>
        <VStack spacing={8} maxW="4xl" mx="auto" textAlign="center">
          <Heading size="4xl" textTransform="uppercase" letterSpacing="wide">
            ARTICLE NOT FOUND
          </Heading>
          <Text color="gray.300">
            The article you're looking for doesn't exist or has been moved.
          </Text>
          <Link to="/articles">
            <Button bg="green.500" color="white" _hover={{ bg: "green.600" }}>
              Back to Articles
            </Button>
          </Link>
        </VStack>
      </Box>
    );
  }

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box py={{ base: 10, md: 20 }} px={4}>
        <VStack spacing={12} maxW="4xl" mx="auto">
          {/* Back to Articles */}
        <HStack w="100%" justify="start">
          <Link to="/articles">
            <Button
              variant="ghost"
              color="green.500"
              size="sm"
              _hover={{ color: "green.400" }}
            >
              ← Back to Articles
            </Button>
          </Link>
        </HStack>

        {/* Article Header */}
        <VStack spacing={6} textAlign="center">
          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <HStack spacing={2}>
              <Box w={2} h={2} bg="green.500" borderRadius="full" />
              <Text color="green.500" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                {article.tags.map(tag => tag.name || tag).join(', ')}
              </Text>
            </HStack>
          )}

          {/* Title */}
          <Heading
            size={{ base: "2xl", md: "4xl" }}
            textTransform="uppercase"
            letterSpacing="wide"
            lineHeight="1.1"
            textAlign={{ base: "left", md: "center" }}
          >
            {article.title}
          </Heading>

          {/* Meta info */}
          <HStack spacing={4} color="gray.400" fontSize="sm">
            {article.published_at && (
              <Text>Published {formatDate(article.published_at)}</Text>
            )}
            <Text>•</Text>
            <Text>{article.read_time || 5} min read</Text>
            {article.views && (
              <>
                <Text>•</Text>
                <Text>{article.views} views</Text>
              </>
            )}
          </HStack>

          {/* Excerpt */}
          {article.excerpt && (
            <Text fontSize="xl" color="gray.300" lineHeight="1.6" maxW="3xl">
              {article.excerpt}
            </Text>
          )}
        </VStack>

        {/* Audio narration player (renders only when audio has been generated) */}
        <ArticleAudioPlayer src={article.audio_file} />

        {/* Featured Image */}
        {article.featured_image && (
          <Box w="100%" borderRadius="md" overflow="hidden">
            <Image
              src={article.featured_image}
              alt={article.title}
              objectFit="contain"
              w="100%"
              maxH="400px"
              mx="auto"
              display="block"
            />
          </Box>
        )}

        {/* Table of Contents */}
        {tocItems.length > 0 && article.show_table_of_contents !== false && (
          <Box
            w="100%"
            bg="gray.900"
            border="1px solid"
            borderColor="gray.700"
            borderRadius="md"
            overflow="hidden"
          >
            <Button
              w="100%"
              py={4}
              px={6}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bg="transparent"
              _hover={{ bg: "gray.800" }}
              onClick={onToggle}
              borderRadius={0}
            >
              <HStack spacing={3}>
                <Box w={1} h={5} bg="green.500" borderRadius="full" />
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  color="white"
                >
                  Table of Contents
                </Text>
              </HStack>
              {isOpen ? (
                <ChevronUpIcon color="gray.400" boxSize={5} />
              ) : (
                <ChevronDownIcon color="gray.400" boxSize={5} />
              )}
            </Button>

            <Collapse in={isOpen}>
              <VStack
                align="start"
                spacing={0}
                px={6}
                pb={4}
                borderTop="1px solid"
                borderColor="gray.800"
              >
                {tocItems.map((item, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    fontWeight="normal"
                    pl={item.level === 1 ? 0 : item.level === 2 ? 4 : 8}
                    py={2}
                    h="auto"
                    justifyContent="start"
                    _hover={{ color: "green.400", bg: "transparent" }}
                    onClick={() => scrollToHeading(item.id)}
                    whiteSpace="normal"
                    textAlign="left"
                  >
                    <HStack spacing={2} align="start">
                      <Box
                        w={1.5}
                        h={1.5}
                        bg="gray.600"
                        borderRadius="full"
                        mt={1.5}
                        flexShrink={0}
                      />
                      <Text fontSize="sm" lineHeight="1.4">
                        {item.text}
                      </Text>
                    </HStack>
                  </Button>
                ))}
              </VStack>
            </Collapse>
          </Box>
        )}

        {/* Article Content */}
        <Box w="100%" ref={contentRef}>
          <Box
            color="gray.300"
            lineHeight="1.8"
            fontSize="lg"
            sx={{
              // Headers
              '& h1': {
                fontSize: '2xl',
                fontWeight: 'bold',
                mt: 8,
                mb: 4,
                color: 'white',
                scrollMarginTop: '100px'
              },
              '& h2': {
                fontSize: 'xl',
                fontWeight: 'bold',
                mt: 6,
                mb: 3,
                color: 'white',
                scrollMarginTop: '100px'
              },
              '& h3': {
                fontSize: 'lg',
                fontWeight: 'bold',
                mt: 4,
                mb: 2,
                color: 'white',
                scrollMarginTop: '100px'
              },

              // Paragraphs
              '& p': { mb: 4, lineHeight: '1.6' },
              '& strong': { fontWeight: 'bold', color: 'white' },
              '& em': { fontStyle: 'italic' },

              // Lists
              '& ul': { pl: 6, mb: 4, listStyleType: 'disc' },
              '& ol': { pl: 6, mb: 4, listStyleType: 'decimal' },
              '& li': { mb: 2 },

              // Links
              '& a': {
                color: 'green.500',
                textDecoration: 'underline',
                _hover: { color: 'green.400' }
              },

              // Code
              '& code': {
                bg: 'gray.800',
                px: 2,
                py: 1,
                borderRadius: 'sm',
                fontSize: 'sm',
                color: 'green.300'
              },
              '& pre': {
                bg: 'gray.800',
                p: 4,
                borderRadius: 'md',
                overflow: 'auto',
                mb: 4
              },

              // Tables
              '& table': {
                w: '100%',
                mb: 4,
                border: '1px solid',
                borderColor: 'gray.600'
              },
              '& th, & td': {
                p: 2,
                border: '1px solid',
                borderColor: 'gray.600'
              },
              '& th': {
                bg: 'gray.800',
                fontWeight: 'bold'
              },

              // Videos
              '& video': {
                display: 'block',
                mx: 'auto',
                maxW: '100%',
                h: 'auto',
                mb: 4,
                borderRadius: 'md'
              },
              '& iframe': {
                display: 'block',
                mx: 'auto',
                maxW: '100%',
                aspectRatio: '16/9',
                mb: 4,
                borderRadius: 'md'
              }
            }}
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        </Box>

        {/* Author info */}
        {article.author && (
          <Box w="100%" pt={8} borderTop="1px solid" borderColor="gray.700">
            <HStack spacing={4}>
              <Box w={12} h={12} bg="gray.700" borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                <Text fontSize="lg">👨‍💻</Text>
              </Box>
              <VStack align="start" spacing={1}>
                <Text fontWeight="bold" color="white">
                  {article.author}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Clover Ridge Technologies
                </Text>
              </VStack>
            </HStack>
          </Box>
        )}

        {/* Back to Articles CTA */}
        <VStack spacing={4} textAlign="center" pt={8}>
          <Link to="/articles">
            <Button bg="green.500" color="white" _hover={{ bg: "green.600" }}>
              Read More Articles
            </Button>
          </Link>
        </VStack>
      </VStack>
    </Box>
  );
};

export default ArticleDetail;
